/**
 * Plans Video Test B - Letterbox Video Player (YouTube-style)
 * 
 * A/B Test variant B: Persistent letterbox video in bottom-right corner
 * 
 * Features:
 * - Auto-plays muted on page load
 * - Click video to fullscreen + unmute (like YouTube preview)
 * - Play/Pause button that also unmutes when playing
 * - Close/minimize functionality
 * - Tab to restore after closing
 */

// Debug mode - set to false in production to disable console logs
const DEBUG_MODE = false;
const log = DEBUG_MODE ? console.log.bind(console) : () => {};

class PlansVideoTestB {
  constructor() {
    // DOM Elements
    this.container = document.getElementById('videoContainer');
    this.video = this.container?.querySelector('video');
    this.videoWrapper = this.container?.querySelector('.video-wrapper');
    this.playPauseBtn = document.getElementById('playPauseBtn');
    this.closeBtn = document.getElementById('closeBtn');
    this.tabButton = document.querySelector('.test-tab-b');
    this.announcements = document.getElementById('videoAnnouncements');

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // State
    this.state = {
      isVisible: true,
      isFullscreen: false,
      isMuted: true,
      isPlaying: false,
      wasClosed: false,           // Track if user manually closed the video
      clickDebounce: false,       // Prevent double-firing from native controls
      clickHandlerDisabled: false // Track if click handler is disabled (for fullscreen)
    };

    // Bound handlers for cleanup
    this.boundKeydownHandler = null;

    // Only initialize if all required elements exist
    if (this.container && this.video) {
      this.init();
    }
  }

  init() {
    // Remove native controls for letterbox mode (too small on mobile)
    // They will be added back when user goes fullscreen
    this.video.removeAttribute('controls');
    
    this.bindEvents();
    this.showLetterbox();
    this.autoPlay(); // Will update play/pause button based on success/failure
    
    log('📹 Plans Video Test B initialized');
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Create bound click handler so we can add/remove it
    this.boundClickHandler = (e) => {
      // SAFETY: Quick state check first (avoid expensive DOM checks)
      if (this.state.clickHandlerDisabled) return;
      
      // Prevent native video controls from also handling this click
      e.preventDefault();
      e.stopPropagation();
      
      // Debounce to prevent double-firing
      if (this.state.clickDebounce) return;
      this.state.clickDebounce = true;
      setTimeout(() => { this.state.clickDebounce = false; }, 100);
      
      this.handleVideoClick();
    };
    
    // Keyboard handler for video wrapper (Enter/Space to activate)
    this.boundWrapperKeyHandler = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleVideoClick();
      }
    };
    
    // Add the click handler
    this.enableVideoClickHandler();

    // Play/Pause button
    this.playPauseBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePlayPause();
    });

    // Close button - always closes the video entirely
    this.closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.state.isFullscreen) {
        // If expanded, close with fade animation from expanded position
        this.closeFromExpanded();
      } else {
        // Close the letterbox entirely (shows tab button)
        this.closeLetterbox();
      }
    });

    // Tab button - open directly into fullscreen mode
    this.tabButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.restoreToFullscreen();
    });

    // Video events - these fire infrequently, no optimization needed
    this.video.addEventListener('play', () => this.onVideoPlay());
    this.video.addEventListener('pause', () => this.onVideoPause());
    this.video.addEventListener('ended', () => this.onVideoEnded());

    // Fullscreen change events
    this.boundFullscreenHandler = () => this.onFullscreenChange();
    document.addEventListener('fullscreenchange', this.boundFullscreenHandler);
    document.addEventListener('webkitfullscreenchange', this.boundFullscreenHandler);
    
    // iOS Safari fullscreen events (video element specific)
    this.video.addEventListener('webkitbeginfullscreen', () => this.onIOSFullscreenEnter());
    this.video.addEventListener('webkitendfullscreen', () => this.onIOSFullscreenExit());

    // Keyboard support - bound handler for potential cleanup
    this.boundKeydownHandler = (e) => this.handleKeydown(e);
    document.addEventListener('keydown', this.boundKeydownHandler);
  }

  /**
   * Enable the video click handler (for letterbox mode)
   */
  enableVideoClickHandler() {
    if (this.videoWrapper && this.boundClickHandler) {
      this.videoWrapper.addEventListener('click', this.boundClickHandler);
      this.videoWrapper.addEventListener('keydown', this.boundWrapperKeyHandler);
      this.state.clickHandlerDisabled = false;
      log('📹 Video click handler enabled');
    }
  }

  /**
   * Disable the video click handler (for fullscreen mode)
   */
  disableVideoClickHandler() {
    if (this.videoWrapper && this.boundClickHandler) {
      this.videoWrapper.removeEventListener('click', this.boundClickHandler);
      this.videoWrapper.removeEventListener('keydown', this.boundWrapperKeyHandler);
      this.state.clickHandlerDisabled = true;
      log('📹 Video click handler disabled');
    }
  }

  /**
   * Handle video click - YouTube style
   * Clicking the video goes fullscreen and unmutes
   */
  handleVideoClick() {
    // Don't trigger when already in fullscreen (use cached state first)
    if (this.state.isFullscreen) return;
    
    log('📹 Video clicked - going fullscreen');
    this.enterFullscreen();
  }

  /**
   * Toggle play/pause via button
   * When playing, also unmutes the video
   */
  togglePlayPause() {
    if (this.state.isPlaying) {
      this.pause();
    } else {
      this.playWithSound();
    }
  }

  /**
   * Play video and unmute
   */
  playWithSound() {
    this.video.muted = false;
    this.state.isMuted = false;
    this.play();
  }

  /**
   * Update the play/pause button icon (SVG)
   */
  updatePlayPauseButton() {
    if (this.playPauseBtn) {
      if (this.state.isPlaying) {
        // Pause icon - two vertical bars
        this.playPauseBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <rect x="6" y="4" width="4" height="16" rx="1"/>
          <rect x="14" y="4" width="4" height="16" rx="1"/>
        </svg>`;
        this.playPauseBtn.title = 'Pause video';
        this.playPauseBtn.setAttribute('aria-label', 'Pause video');
        this.playPauseBtn.setAttribute('aria-pressed', 'true');
      } else {
        // Play icon - triangle pointing right
        this.playPauseBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M8 5.14v13.72c0 .94 1.02 1.52 1.83 1.04l11.06-6.86c.78-.48.78-1.6 0-2.08L9.83 4.1C9.02 3.62 8 4.2 8 5.14z"/>
        </svg>`;
        this.playPauseBtn.title = 'Play video';
        this.playPauseBtn.setAttribute('aria-label', 'Play video');
        this.playPauseBtn.setAttribute('aria-pressed', 'false');
      }
    }
  }

  /**
   * Announce message to screen readers
   */
  announce(message) {
    if (this.announcements) {
      this.announcements.textContent = message;
      // Clear after announcement to allow repeat announcements
      setTimeout(() => {
        this.announcements.textContent = '';
      }, 1000);
    }
  }

  /**
   * Auto-play video muted on load
   * Respects prefers-reduced-motion accessibility preference
   */
  autoPlay() {
    // Respect reduced motion preference - don't autoplay
    if (this.prefersReducedMotion) {
      log('📹 Auto-play skipped - user prefers reduced motion');
      this.state.isPlaying = false;
      this.updatePlayPauseButton();
      return;
    }

    this.video.muted = true;
    this.state.isMuted = true;

    // Attempt autoplay
    const playPromise = this.video.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          log('📹 Auto-play started');
          this.state.isPlaying = true;
          this.updatePlayPauseButton();
        })
        .catch((error) => {
          log('📹 Auto-play prevented:', error);
          this.state.isPlaying = false;
          this.updatePlayPauseButton();
          this.showPlayPrompt();
        });
    }
  }

  /**
   * Play video
   */
  play() {
    this.video.play()
      .then(() => {
        this.state.isPlaying = true;
      })
      .catch(() => {
        // Silently handle play failures
      });
  }

  /**
   * Pause video
   */
  pause() {
    this.video.pause();
    this.state.isPlaying = false;
  }

  /**
   * Restart video from beginning with sound
   */
  restartWithSound() {
    this.video.currentTime = 0;
    this.video.muted = false;
    this.state.isMuted = false;
    this.play();
  }

  /**
   * Toggle muted state
   */
  toggleMute() {
    this.video.muted = !this.video.muted;
    this.state.isMuted = this.video.muted;
  }

  /**
   * Show a play prompt when autoplay is blocked
   */
  showPlayPrompt() {
    this.container.classList.add('autoplay-blocked');
  }

  /**
   * Show the letterbox video container
   */
  showLetterbox() {
    this.container.classList.add('visible');
    this.container.classList.remove('hidden');
    this.state.isVisible = true;
    this.state.wasClosed = false;
  }

  /**
   * Close/hide the letterbox video
   */
  closeLetterbox() {
    this.pause();
    this.container.classList.remove('visible', 'expanded');
    this.container.classList.add('hidden');
    this.state.isVisible = false;
    this.state.isExpanded = false;
    this.state.wasClosed = true;

    // Update tab button to indicate video can be restored
    if (this.tabButton) {
      this.tabButton.classList.add('video-available');
      this.tabButton.setAttribute('aria-hidden', 'false');
    }
    
    // Announce to screen readers
    const tabButtonLabel = this.tabButton ? this.tabButton.textContent.trim() : 'Plan Differences';
    this.announce(`Video player closed. Select ${tabButtonLabel} button to reopen.`);
    
    // Move focus to tab button for keyboard users
    if (this.tabButton) {
      // Remove aria-hidden BEFORE focusing (can't focus hidden elements)
      this.tabButton.removeAttribute('aria-hidden');
      this.tabButton.focus();
    }
    
    log('📹 Letterbox closed');
  }

  /**
   * Close from expanded mode with fade animation
   * Animates out from the expanded position, then cleans up
   */
  closeFromExpanded() {
    this.pause();
    
    // Hide the modal overlay immediately
    const overlay = document.getElementById('modal-overlay');
    if (overlay) {
      overlay.classList.remove('active');
      if (this.boundOverlayClickHandler) {
        overlay.removeEventListener('click', this.boundOverlayClickHandler);
        this.boundOverlayClickHandler = null;
      }
    }
    
    // Trigger the close animation while still in expanded position
    this.container.classList.remove('visible');
    this.container.classList.add('hidden');
    this.state.isVisible = false;
    this.state.isFullscreen = false;
    this.state.wasClosed = true;
    
    // Clean up expanded state after animation completes (300ms transition)
    setTimeout(() => {
      this.container.classList.remove('is-expanded');
      this.video.removeAttribute('controls');
      
      // Restore video wrapper accessibility for next time
      if (this.videoWrapper) {
        this.videoWrapper.setAttribute('tabindex', '0');
        this.videoWrapper.removeAttribute('aria-hidden');
        this.videoWrapper.setAttribute('aria-label', 'Click to expand video to fullscreen');
      }
      
      // Restore container accessibility
      this.container.setAttribute('role', 'region');
      this.container.removeAttribute('aria-modal');
      this.container.setAttribute('aria-label', 'Video preview player');
      
      // Restore close button label
      if (this.closeBtn) {
        this.closeBtn.setAttribute('aria-label', 'Close video player');
      }
      
      // Restore looping for next time
      this.video.setAttribute('loop', '');
      
      // Re-enable click handler for next time
      this.enableVideoClickHandler();
    }, 300);
    
    // Update tab button to indicate video can be restored
    if (this.tabButton) {
      this.tabButton.classList.add('video-available');
      this.tabButton.removeAttribute('aria-hidden');
      this.tabButton.focus();
    }
    
    // Announce to screen readers
    const tabButtonLabel = this.tabButton ? this.tabButton.textContent.trim() : 'Plan Differences';
    this.announce(`Video player closed. Select ${tabButtonLabel} button to reopen.`);
    
    log('📹 Closed from expanded mode');
  }

  /**
   * Restore letterbox after it was closed (via tab button)
   */
  restoreLetterbox() {
    this.showLetterbox();
    
    // Reset video state if it ended
    if (this.video.ended) {
      this.video.currentTime = 0;
    }
    
    // Auto-play muted when restoring (unless user prefers reduced motion)
    this.video.muted = true;
    this.state.isMuted = true;
    
    if (!this.prefersReducedMotion) {
      this.play();
    }

    // Hide tab button again (no aria-hidden needed, CSS handles visibility)
    if (this.tabButton) {
      this.tabButton.classList.remove('video-available');
    }
    
    // Announce to screen readers
    this.announce('Video player opened.');
    
    // Move focus to play/pause button after transition completes
    // Small delay ensures the container is fully visible first
    setTimeout(() => {
      if (this.playPauseBtn) {
        this.playPauseBtn.focus();
      }
    }, 50);
    
    log('📹 Letterbox restored');
  }

  /**
   * Restore directly to fullscreen mode (via tab button)
   */
  restoreToFullscreen() {
    // Make container visible first (required for fullscreen)
    this.container.classList.add('visible');
    this.container.classList.remove('hidden');
    this.state.isVisible = true;
    this.state.wasClosed = false;
    
    // Hide tab button
    if (this.tabButton) {
      this.tabButton.classList.remove('video-available');
    }
    
    // Enter fullscreen mode directly
    this.enterFullscreen();
    
    log('📹 Restored directly to fullscreen');
  }

  /**
   * Enter expanded mode (modal-style, like Test A)
   * Expands video to center of screen with overlay behind it
   */
  enterFullscreen() {
    // Restart video from the beginning
    this.video.currentTime = 0;
    
    // Unmute when expanding and set volume to 75% (less jarring than full volume)
    this.video.muted = false;
    this.video.volume = 0.75;
    this.state.isMuted = false;
    
    // Show native controls in expanded mode
    this.video.setAttribute('controls', '');
    
    // Stop looping in expanded mode (play through once)
    this.video.removeAttribute('loop');
    
    // Add expanded class to container for CSS targeting
    this.container.classList.add('is-expanded');
    
    // Show the modal overlay
    const overlay = document.getElementById('modal-overlay');
    if (overlay) {
      overlay.classList.add('active');
      // Add click handler to close when clicking overlay
      this.boundOverlayClickHandler = (e) => {
        if (e.target === overlay) {
          this.exitFullscreen();
        }
      };
      overlay.addEventListener('click', this.boundOverlayClickHandler);
    }
    
    // Disable click handler to prevent letterbox behavior in expanded mode
    this.disableVideoClickHandler();
    
    // Remove video wrapper from tab order (users can tab directly to video controls)
    if (this.videoWrapper) {
      this.videoWrapper.setAttribute('tabindex', '-1');
      this.videoWrapper.setAttribute('aria-hidden', 'true');
    }
    
    // Update container accessibility for modal/dialog behavior
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    this.container.setAttribute('aria-label', 'Video player expanded');
    
    // Update close button label for expanded context
    if (this.closeBtn) {
      this.closeBtn.setAttribute('aria-label', 'Close expanded video');
    }
    
    // Play the video
    this.play();
    
    this.state.isFullscreen = true;
    
    // Announce to screen readers
    this.announce('Video expanded. Press Escape to close.');
    
    // Move focus to close button for keyboard accessibility
    // Small delay ensures the expanded state transition completes first
    setTimeout(() => {
      if (this.closeBtn) {
        this.closeBtn.focus();
      }
    }, 50);
    
    log('📹 Video expanded (modal style)');
  }

  /**
   * Exit expanded mode - return to letterbox
   */
  exitFullscreen() {
    // Remove expanded class
    this.container.classList.remove('is-expanded');
    
    // Hide the modal overlay
    const overlay = document.getElementById('modal-overlay');
    if (overlay) {
      overlay.classList.remove('active');
      // Remove overlay click handler
      if (this.boundOverlayClickHandler) {
        overlay.removeEventListener('click', this.boundOverlayClickHandler);
        this.boundOverlayClickHandler = null;
      }
    }
    
    // Remove native controls for letterbox mode
    this.video.removeAttribute('controls');
    
    // Restore looping for letterbox mode
    this.video.setAttribute('loop', '');
    
    // Re-enable click handler for letterbox mode
    this.enableVideoClickHandler();
    
    // Restore video wrapper to tab order and accessibility for letterbox mode
    if (this.videoWrapper) {
      this.videoWrapper.setAttribute('tabindex', '0');
      this.videoWrapper.removeAttribute('aria-hidden');
      this.videoWrapper.setAttribute('aria-label', 'Click to expand video to fullscreen');
    }
    
    // Restore container accessibility for letterbox mode
    this.container.setAttribute('role', 'region');
    this.container.removeAttribute('aria-modal');
    this.container.setAttribute('aria-label', 'Video preview player');
    
    // Restore close button label for letterbox context
    if (this.closeBtn) {
      this.closeBtn.setAttribute('aria-label', 'Close video player');
    }
    
    // Announce to screen readers
    this.announce('Video minimized to preview.');
    
    this.state.isFullscreen = false;
    log('📹 Video collapsed to letterbox');
  }

  /**
   * Handle fullscreen change event (for native fullscreen fallback)
   * Called when user exits fullscreen (via Escape or native controls)
   */
  onFullscreenChange() {
    // Only handle if we're in native fullscreen (not our modal-style expanded)
    const isNativeFullscreen = !!(
      document.fullscreenElement || 
      document.webkitFullscreenElement || 
      document.msFullscreenElement
    );
    
    // If exiting native fullscreen, sync our state
    if (!isNativeFullscreen && this.state.isFullscreen && !this.container.classList.contains('is-expanded')) {
      this.state.isFullscreen = false;
      this.video.removeAttribute('controls');
      this.enableVideoClickHandler();
      log('📹 Video exited native fullscreen');
    }
  }

  /**
   * Handle iOS Safari fullscreen enter (fallback for iOS)
   * iOS may still use native fullscreen for video elements
   */
  onIOSFullscreenEnter() {
    this.state.isFullscreen = true;
    log('📹 iOS fullscreen entered');
  }

  /**
   * Handle iOS Safari fullscreen exit
   */
  onIOSFullscreenExit() {
    this.state.isFullscreen = false;
    this.video.removeAttribute('controls');
    this.enableVideoClickHandler();
    log('📹 iOS fullscreen exited');
  }

  /**
   * Check if currently in expanded/fullscreen mode
   */
  isInFullscreen() {
    return this.state.isFullscreen || this.container.classList.contains('is-expanded');
  }

  /**
   * Handle video play event
   */
  onVideoPlay() {
    this.state.isPlaying = true;
    this.container.classList.remove('paused', 'ended');
    this.container.classList.add('playing');
    this.updatePlayPauseButton();
  }

  /**
   * Handle video pause event
   */
  onVideoPause() {
    this.state.isPlaying = false;
    this.container.classList.remove('playing');
    this.container.classList.add('paused');
    this.updatePlayPauseButton();
  }

  /**
   * Handle video ended event
   */
  onVideoEnded() {
    this.state.isPlaying = false;
    this.container.classList.remove('playing');
    this.container.classList.add('ended');
    this.updatePlayPauseButton();
  }

  /**
   * Handle keyboard shortcuts
   */
  handleKeydown(e) {
    // Quick exit for common keys that we don't handle
    if (!this.state.isVisible) return;
    
    // Only process specific keys to reduce overhead
    const key = e.key;
    if (key !== 'Escape' && key !== ' ' && key !== 'f' && key !== 'F' && key !== 'm' && key !== 'M') {
      return;
    }

    switch (key) {
      case 'Escape':
        if (this.state.isFullscreen) {
          // Close expanded view (return to letterbox)
          this.exitFullscreen();
        } else {
          // Close letterbox entirely
          this.closeLetterbox();
        }
        break;
      case ' ':
        if (document.activeElement === this.video || document.activeElement === this.container) {
          e.preventDefault();
          this.togglePlayPause();
        }
        break;
      case 'f':
      case 'F':
        if (document.activeElement === this.video || document.activeElement === this.container) {
          e.preventDefault();
          // Toggle expanded mode
          if (this.state.isFullscreen) {
            this.exitFullscreen();
          } else {
            this.enterFullscreen();
          }
        }
        break;
      case 'm':
      case 'M':
        if (document.activeElement === this.video || document.activeElement === this.container) {
          this.toggleMute();
        }
        break;
    }
  }

  /**
   * Get current state (useful for debugging/analytics)
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Destroy instance and cleanup
   */
  destroy() {
    this.pause();
    this.container.classList.remove('visible', 'expanded', 'hidden');
    
    // Remove event listeners
    if (this.boundKeydownHandler) {
      document.removeEventListener('keydown', this.boundKeydownHandler);
    }
    if (this.boundFullscreenHandler) {
      document.removeEventListener('fullscreenchange', this.boundFullscreenHandler);
      document.removeEventListener('webkitfullscreenchange', this.boundFullscreenHandler);
    }
    
    log('📹 Plans Video Test B destroyed');
  }
}

/**
 * Initialization Controller
 * Watches for 'plans-video-test-b' class on body (added by Convert A/B testing)
 */
const PlansVideoTestBController = {
  initialized: false,
  observer: null,
  TEST_CLASS: 'plans-video-test-b',

  /**
   * Check if test class is present on body
   */
  isTestActive() {
    return document.body.classList.contains(this.TEST_CLASS);
  },

  /**
   * Check if required DOM elements exist
   */
  hasRequiredElements() {
    return document.querySelector('.video-test-b') !== null;
  },

  /**
   * Initialize the video player
   */
  initializePlayer() {
    if (this.initialized) return;
    
    if (!this.hasRequiredElements()) {
      log('📹 Test B: Required elements not found, skipping initialization');
      return;
    }

    window.plansVideoTestB = new PlansVideoTestB();
    this.initialized = true;
    
    // Clean up observer if it exists
    this.stopObserving();
  },

  /**
   * Start observing body for class changes
   */
  startObserving() {
    // Don't observe if already initialized
    if (this.initialized) return;

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (this.isTestActive()) {
            log('📹 Test B: Convert class detected, initializing...');
            this.initializePlayer();
            break;
          }
        }
      }
    });

    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    log('📹 Test B: Watching for Convert class on body...');
  },

  /**
   * Stop observing and clean up
   */
  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      log('📹 Test B: Observer disconnected');
    }
  },

  /**
   * Main entry point
   */
  start() {
    // Check if class is already present (Convert may have run before our script)
    if (this.isTestActive()) {
      log('📹 Test B: Convert class already present, initializing...');
      this.initializePlayer();
      return;
    }

    // Otherwise, start observing for when Convert adds the class
    this.startObserving();

    // Safety timeout: stop observing after 30 seconds if Convert never fires
    setTimeout(() => {
      if (!this.initialized) {
        this.stopObserving();
        log('📹 Test B: Timeout reached, stopping observer');
      }
    }, 30000);
  }
};

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => PlansVideoTestBController.start());
} else {
  // DOM already loaded
  PlansVideoTestBController.start();
}

